import React, {
  memo,
  ChangeEvent,
  FormEvent,
  useState,
  useMemo,
  useCallback,
  KeyboardEvent,
} from 'react'
import { useSelector } from 'react-redux'
import { InlineMessage } from 'transfix-ui/components/InlineMessage/InlineMessage'
import SuggestLocation, { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { IFacility, IFacilityAddress } from 'apps/fleet-planner/utils/api/facilities'
import { CreateFacilityAddressSchema } from 'apps/fleet-planner/utils/createFacilityAddressValidator'
import { recentFacilitiesStateSelector } from 'apps/fleet-planner/utils/selectors/facilities'
import { TextInput } from 'components/atoms'
import { Checkbox } from 'components/forms'

interface IFacilityAddressForm {
  changeHandler: (change: CreateFacilityAddressSchema) => void
  addFacilityAddress: () => void
  cancelFacilityAddress: () => void
  facilityAddress: Partial<IFacilityAddress>
  isEditing: boolean
  errors?: {
    [key: string]: string
  }
}

const AddFacilityAddressForm = memo<IFacilityAddressForm>(
  ({ changeHandler, addFacilityAddress, facilityAddress = {}, errors }) => {
    const { facilities } = useSelector(recentFacilitiesStateSelector)

    const [isNameInputVisible, setNameInputVisible] = useState(false)

    const locationInputChangeHandler = ({
      city,
      postalCode: zip,
      state,
    }: ILocationSuggestion): void => {
      changeHandler({
        locality: city ?? '',
        subdivisionCode: state ?? '',
        postalCode: zip ?? '',
      })
    }

    const inputChangeHandler = (e: ChangeEvent<HTMLInputElement>): void => {
      changeHandler({ [e.target.id]: e.target.value })
    }

    const setFacility = (facility: IFacility) => {
      const { addresses } = facility
      const {
        addressLine1 = '',
        addressLine2 = '',
        addressee = '',
        locality = '',
        postalCode = '',
        subdivisionCode = '',
      } = addresses[0]

      changeHandler({
        addressee,
        addressLine1,
        addressLine2,
        subdivisionCode,
        locality,
        postalCode,
      })
    }

    const formSubmitHandler = (e: FormEvent<HTMLFormElement>): void => {
      e.preventDefault()
      addFacilityAddress()
    }

    const checkboxChangeHandler = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
      changeHandler({
        isSavingFacility: true,
      })
      setNameInputVisible(checked)
    }

    const findFacilityMatchingAddress = useCallback(
      (addressee: string | null): IFacility | undefined =>
        facilities.find(
          facility => facility.name.toLowerCase().trim() === `${addressee}`.toLowerCase().trim()
        ),
      [facilities]
    )

    const duplicateFacility = useMemo(
      () => findFacilityMatchingAddress(`${facilityAddress.addressee}`),
      [facilityAddress.addressee, findFacilityMatchingAddress]
    )

    const getAlertMessage = () => {
      const { addressee: addresseeInput = '' } = facilityAddress
      const existingFacility = findFacilityMatchingAddress(addresseeInput)

      if (!existingFacility || !existingFacility.addresses || !existingFacility.addresses[0]) {
        throw new Error('Invalid existing facility')
      }

      const { addresses, name } = existingFacility
      const { addressLine1, addressLine2, addressee, locality, postalCode, subdivisionCode } =
        addresses[0] || {}
      const line2 = addressLine2 ? ` ${addressLine2}` : ''
      return `${
        name || addressee
      } · ${addressLine1}${line2}, ${locality}, ${subdivisionCode} ${postalCode}`
    }

    const setToExistingFacility = () => {
      const { addressee = '' } = facilityAddress
      const existingFacility = findFacilityMatchingAddress(addressee)
      if (!existingFacility?.id) {
        throw new Error('Missing facility id')
      }
      setFacility(existingFacility)
    }

    return (
      <div id='add-new-facility-address'>
        <form
          onSubmit={formSubmitHandler}
          name='add-new-facility-address-form'
          className='add-new-facility-address-form'
          id='add-new-facility-address-form'>
          {/* Address line 1 */}
          <div className='row'>
            <div className='col-md-12' data-test='facility-address-line-1'>
              <TextInput
                autoComplete='off'
                clearable={false}
                autoFocus={!facilityAddress.addressLine1}
                dataTest='address-line-1-input'
                id='addressLine1'
                invalid={!!errors?.addressLine1}
                label='Address Line 1'
                large
                onChange={inputChangeHandler}
                required={isNameInputVisible} // if box is checked, this is required
                type='text'
                validationMessage={errors?.addressLine1}
                value={facilityAddress.addressLine1}
              />
            </div>
          </div>

          {/* Address line 2 */}
          <div className='row'>
            <div className='col-md-12' data-test='facility-address-line-2'>
              <TextInput
                autoComplete='off'
                clearable={false}
                dataTest='address-line-2-input'
                id='addressLine2'
                invalid={!!errors?.addressLine2}
                label='Address Line 2'
                placeholder='Apt 5'
                large
                onChange={inputChangeHandler}
                type='text'
                validationMessage={errors?.addressLine2}
                value={facilityAddress.addressLine2 || ''}
              />
            </div>
          </div>

          {/* City */}
          <div className='row'>
            <div className='col-md-9' data-test='facility-city'>
              <SuggestLocation
                dataTest='facility-city-input'
                id='city'
                label='City'
                large
                resultTypes={['City', 'State']}
                onChange={locationInputChangeHandler}
                placeholder='Choose location'
                required
                clearable={false}
                errorMessage={errors?.locality || errors?.subdivisionCode || ''}
                value={{
                  city: facilityAddress.locality || '',
                  state: facilityAddress.subdivisionCode || '',
                }}
              />
            </div>
            <div className='col-md-3' data-test='facility-postalCode'>
              <TextInput
                autoComplete='off'
                clearable={false}
                dataTest='facility-postalCode'
                id='postalCode'
                invalid={!!errors?.postalCode}
                label='Zip Code'
                large
                maxLength={5}
                name='facility.location.postalCode'
                onChange={inputChangeHandler}
                placeholder='e.g 76473'
                validationMessage={errors?.postalCode}
                value={facilityAddress.postalCode || ''}
              />
            </div>
          </div>

          <div className='row'>
            <div className='col-md-12'>
              <Checkbox
                label='Save this facility address'
                checked={isNameInputVisible}
                onChange={checkboxChangeHandler}
                onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => {
                  e.preventDefault()
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  e.preventDefault()
                  setNameInputVisible(!isNameInputVisible)
                }}
                id='save-facility-address'
                dataTest='save-facility-address'
              />
            </div>
          </div>

          {isNameInputVisible && (
            <>
              <div className='row'>
                <div className='col-md-12'>
                  <div className='mt2 mb1'>
                    <div className='b'>Provide a name for this facility</div>
                    <div>
                      You’ll be able to select this facility when choosing a pickup or dropoff
                      location.
                    </div>
                  </div>
                </div>
              </div>
              <div className='row'>
                <div className='col-md-12' data-test='facility-name'>
                  <TextInput
                    autoFocus={!facilityAddress.addressee}
                    autoComplete='off'
                    clearable={false}
                    dataTest='facility-name'
                    id='addressee'
                    invalid={!!errors?.addressee}
                    label='Facility Name'
                    large
                    required
                    name='facility.addressee'
                    onChange={inputChangeHandler}
                    placeholder='Houston Warehouse'
                    validationMessage={errors?.addressee}
                    value={`${facilityAddress.addressee}`}
                  />
                </div>
              </div>
            </>
          )}

          {isNameInputVisible && !!duplicateFacility && (
            <div className='row'>
              <div className='col-md-12'>
                <InlineMessage
                  className='mt2'
                  cta={{
                    action: setToExistingFacility,
                    label: 'Select this facility',
                  }}
                  title='Would you like to use this facility instead?'
                  message={getAlertMessage()}
                />
              </div>
            </div>
          )}
        </form>
      </div>
    )
  }
)

export default AddFacilityAddressForm
